<template>
  <LeafletMap
    ref="map"
    :zoom-control="false"
    :scroll-wheel-zoom="false"
    :attribution-control="false"
    :layers="layers"
    :fit-bounds="fitBounds"
    :fit-options="fitOptions"
  />
</template>

<script>
import LeafletMap from '@/components/LeafletMap.vue'
import { mapGetters } from 'vuex'
import {
  Icon,
  Polyline,
  TileLayer,
  latLng,
  Marker,
  DivIcon
} from 'leaflet'
import { length } from 'leaflet-geometryutil'
import makeTileLayerUrl, { defaultOptions } from '@/util/map/makeTileLayerUrl'
import MovingMarker from '@/leaflet/MovingMarker'
import CatcherCar from '@/assets/map/catcher-car-90deg.png'

export default {
  name: 'RunMap',
  components: {
    LeafletMap
  },
  inject: ['media'],
  props: {
    id: {
      type: String,
      default: undefined
    },
    track: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      baseLayers: [
        new TileLayer(makeTileLayerUrl({
          params: {
            lg: this.$i18n.locale
          }
        }), defaultOptions),
        new Polyline(this.track, {
          color: '#0484BA',
          weight: 3,
          opacity: 1
        }),
        new Marker(this.track[this.track.length - 1] || [0, 0], {
          icon: new DivIcon({
            iconSize: [26, 26],
            className: this.$style.position
          })
        })
      ],
      ccMarker: null
    }
  },
  computed: {
    ...mapGetters([
      'catcherCarDistance',
      'catcherCarSpeed'
    ]),
    layers() {
      return [
        ...this.baseLayers,
        this.getCcMarker()
      ]
    },
    distance() {
      return length(this.layers[1])
    },
    fitBounds() {
      const trackLayer = this.layers[1]
      if (!trackLayer) {
        return null
      }

      const bounds = trackLayer.getBounds()
      if (!bounds.isValid()) {
        return null
      }
      return bounds
    },
    fitOptions() {
      return { paddingBottomRight: [0, 0] }
    }
  },
  watch: {
    '$i18n.locale': 'updateTileLayerUrl',
    track(track) {
      this.layers[1].setLatLngs(track)
      this.updateMarkers()
    },
    catcherCarDistance: 'updateMarkers'
  },
  methods: {
    updateTileLayerUrl() {
      const layer = this.layers[0]
      layer.setUrl(makeTileLayerUrl({
        params: {
          lg: this.$i18n.locale
        }
      }))
    },
    updateMarkers() {
      const [, track, positionMarker, ccMarker] = this.layers
      positionMarker.setLatLng(this.track[this.track.length - 1])
      // eslint-disable-next-line no-underscore-dangle
      const positionIcon = positionMarker._icon
      if (positionIcon) {
        positionIcon.title = this.$formatDistance((this.distance / 1000).toFixed(3).slice(0, -2))
      }

      ccMarker
        .setLine(track.getLatLngs())
        .setDistance(this.catcherCarDistance, this.catcherCarSpeed)
        .setTitle(
          this.$formatDistance((this.catcherCarDistance / 1000).toFixed(3).slice(0, -2))
        )
        .start()
    },
    getCcMarker() {
      if (!this.ccMarker || this.ccMarker.$key !== this.id) {
        this.ccMarker = new MovingMarker(this.track.map(point => latLng(point)), {
          icon: new Icon({
            iconSize: [44, 22],
            iconUrl: CatcherCar
          }),
          distance: this.catcherCarDistance,
          speed: 0,
          showPath: false,
          zIndexOffset: -50
        })
        this.ccMarker.$key = this.id
      }

      return this.ccMarker
    }
  }
}
</script>

<style lang="scss" module>
.position {
  background-color: $cyan;
  border: .1875rem solid $white;
  border-radius: 9999px;
  position: relative;
  z-index: 403;

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 3rem;
    height: 3rem;
    background-color: rgba(#3E83F7, .28);
    border-radius: inherit;
    transform: translate3d(-50%, -50%, 0);
    z-index: 402;
    pointer-events: none;

    animation: pulsate 2s cubic-bezier(.74, .01, .69, .42);
    animation-iteration-count: infinite;
    animation-fill-mode: both;
  }
}

@keyframes pulsate {
  0% {
    transform: translate3d(-50%, -50%, 0) scale(0.1, 0.1);
    opacity: 0
  }
  66% {
    opacity: 1.0
  }
  100% {
    transform: translate3d(-50%, -50%, 0) scale(1.7, 1.7);
    opacity: 0
  }
}
</style>
